'use client';

import { useEffect, useRef } from 'react';
import {
	configureMetaPixel,
	getPageTypeFromPathname,
	installWorkmanAnalytics,
	trackAnalyticsEvent,
} from '@/lib/analytics/client';
import { useBrowserLocation } from '@/lib/use-browser-location';

export function StorefrontAnalytics({ metaPixelId }: { metaPixelId?: string | null }) {
	// Deliberately NOT usePathname()/useSearchParams(): the URL only drives the
	// page_view effect below, and the Next hooks are URL data — rendered in the
	// [market] layout they made every store route a partial prerender (the
	// search-params read suspends the prerender into a runtime hole).
	// useBrowserLocation follows soft navigations through the history patch
	// (Next commits every navigation with pushState/replaceState), so the
	// page_view-per-route-key semantics are unchanged; it is empty until mount.
	const { pathname, search } = useBrowserLocation();
	const lastTrackedPathRef = useRef<string | null>(null);

	useEffect(() => {
		configureMetaPixel(metaPixelId);
		installWorkmanAnalytics();
	}, [metaPixelId]);

	useEffect(() => {
		if (!pathname) return;

		const nextPath = getPageViewRouteKey(pathname, search);
		if (lastTrackedPathRef.current === nextPath) return;

		lastTrackedPathRef.current = nextPath;
		const frame = window.requestAnimationFrame(() => {
			trackAnalyticsEvent({
				name: 'page_view',
				pageType: getPageTypeFromPathname(pathname),
				source: 'route_change',
			});
		});

		return () => window.cancelAnimationFrame(frame);
	}, [pathname, search]);

	return null;
}

function getPageViewRouteKey(pathname: string, search: string) {
	// `search` is window.location.search (leading `?`), which URLSearchParams
	// accepts and re-serializes identically to useSearchParams().toString().
	if (!search || search === '?') return pathname;

	const params = new URLSearchParams(search);
	if (isProductPath(pathname)) {
		params.delete('variant');
		for (const key of Array.from(params.keys())) {
			if (key.startsWith('option.')) params.delete(key);
		}
	}
	params.sort();

	const normalizedSearch = params.toString();
	return normalizedSearch ? `${pathname}?${normalizedSearch}` : pathname;
}

function isProductPath(pathname: string) {
	return /\/products\/[^/]+\/?$/.test(pathname);
}
